// Client-side tab filter for the collection grid. Tabs carry a data-filter
// value; product cells expose a data-filter string (product type + tags).
// "all" shows everything; promo tiles show only under "all".
//
// Deep-linking: the active filter is mirrored in the URL as ?filter=<value>,
// so a filtered view can be linked directly from other pages (e.g.
// /collections/all?filter=beard). On load the tab named in ?filter is
// activated; clicking a tab updates the URL (and back/forward re-applies it).
class CollectionFilter extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.col-grid__tab'));
    this.cells = Array.from(this.querySelectorAll('.col-grid__cell'));
    if (this.tabs.length === 0) return;
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.apply(tab, true)));

    // Activate whatever ?filter= names on load, and keep in sync with history.
    this.applyFromUrl();
    window.addEventListener('popstate', () => this.applyFromUrl());
  }

  /** Find the tab matching a filter value (case-insensitive); null if none. */
  tabFor(value) {
    const v = (value || '').trim().toLowerCase();
    const isAll = (t) => (t.dataset.filter || 'all').trim().toLowerCase() === 'all';
    if (!v || v === 'all') return this.tabs.find(isAll) || this.tabs[0];
    return this.tabs.find((t) => (t.dataset.filter || '').trim().toLowerCase() === v) || null;
  }

  /** Read ?filter= from the URL and activate the matching tab (or "all"). */
  applyFromUrl() {
    const param = new URLSearchParams(window.location.search).get('filter');
    const tab = this.tabFor(param) || this.tabFor('all');
    if (tab) this.apply(tab, false);
  }

  apply(tab, updateUrl) {
    const filter = (tab.dataset.filter || 'all').trim();
    this.tabs.forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
    this.cells.forEach((cell) => {
      if (filter === 'all') {
        cell.hidden = false;
        return;
      }
      const card = cell.querySelector('.tpc');
      const types = card ? card.dataset.filter || '' : '';
      cell.hidden = !types.includes(filter.toLowerCase());
    });
    if (updateUrl) this.updateUrl(filter);
  }

  /** Reflect the active filter in the URL without reloading the page. */
  updateUrl(filter) {
    const url = new URL(window.location.href);
    if (!filter || filter.toLowerCase() === 'all') {
      url.searchParams.delete('filter');
    } else {
      url.searchParams.set('filter', filter);
    }
    window.history.pushState({ filter }, '', url);
  }
}

if (!customElements.get('collection-filter')) {
  customElements.define('collection-filter', CollectionFilter);
}
